import type { LineageItem } from '../types/lineage-images.api.types';
import type { LineageJoinTarget, LineageStageJoin } from '../types/lineage-images.types';

/**
 * Extra pages one stage may read beyond the aligned page to find a listing's item. Past this the
 * cell shows "…" (Not loaded) rather than walking the whole stage.
 */
export const MAX_EXTRA_STAGE_PAGES = 3;

/**
 * A listing's identity across stages: (feed product, scope). Every stage files the same feed
 * product under the same scope; the catalog product id stands in for a row without a feed id.
 */
export function lineageListingKey(item: {
  feedProductId: string | null;
  productId: string;
  scope: string;
}): string {
  return `${item.feedProductId ?? item.productId}|${item.scope}`;
}

/**
 * Where a listing stands against `rows`, a contiguous window of one stage's pages:
 * - `found` (the newest attempt seen; `final` once the window runs past the listing's group, so
 *   no newer attempt can follow on the next page),
 * - `absent` (the window provably spans the listing's position and it has no item),
 * - `before` / `after` (it would sit on an earlier / later page).
 */
type TargetState =
  | { kind: 'found'; item: LineageItem; final: boolean }
  | { kind: 'absent' }
  | { kind: 'before' }
  | { kind: 'after' };

/**
 * Locates one listing in a window of rows. The lineage keyset orders a stage by (productId, scope,
 * attempt) — verified on dev — and product ids are uuids, whose string order matches the
 * database's; scope order is collation-dependent, so it is never compared, only positions are.
 */
export function locateTarget(
  rows: readonly LineageItem[],
  target: LineageJoinTarget,
  atFirstPage: boolean,
  atLastPage: boolean,
): TargetState {
  let newest: LineageItem | undefined;
  let lastIndex = -1;
  for (const [index, item] of rows.entries()) {
    if (lineageListingKey(item) !== target.key) continue;
    lastIndex = index;
    if (!newest || item.attempt > newest.attempt) newest = item;
  }
  if (newest) {
    // Final once a later item leaves this (product, scope) group — its attempts are contiguous.
    const passed = rows
      .slice(lastIndex + 1)
      .some((item) => item.productId !== target.productId || item.scope !== target.scope);
    return { kind: 'found', item: newest, final: atLastPage || passed };
  }

  const first = rows[0]?.productId;
  const last = rows[rows.length - 1]?.productId;
  if (first === undefined || last === undefined) {
    return atFirstPage && atLastPage ? { kind: 'absent' } : { kind: 'before' };
  }
  // At or before the window's first product: the group may begin on the previous page.
  if (target.productId <= first && !atFirstPage) return { kind: 'before' };
  // At or after the window's last product: the group may continue on the next page.
  if (target.productId >= last && !atLastPage) return { kind: 'after' };
  return { kind: 'absent' };
}

/**
 * Settles every target against the window: found items (newest attempt, even if the extra-page
 * cap stopped short of proving it final), `null` for proven absences; unresolved keys are left
 * out so the grid shows "Not loaded" instead of a false "—".
 */
export function resolveTargets(
  rows: readonly LineageItem[],
  targets: readonly LineageJoinTarget[],
  atFirstPage: boolean,
  atLastPage: boolean,
): { join: LineageStageJoin; needBefore: boolean; needAfter: boolean } {
  const join: LineageStageJoin = {};
  let needBefore = false;
  let needAfter = false;
  for (const target of targets) {
    const state = locateTarget(rows, target, atFirstPage, atLastPage);
    if (state.kind === 'found') {
      join[target.key] = state.item;
      if (!state.final) needAfter = true;
    } else if (state.kind === 'absent') {
      join[target.key] = null;
    } else if (state.kind === 'before') {
      needBefore = true;
    } else {
      needAfter = true;
    }
  }
  return { join, needBefore, needAfter };
}
